import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FRESHDESK, MainPageNavigatorConstants as ui } from '../../Config/UIConstants';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { EmptyContentOnTabDisplay } from '../../SharedComponents/EmptyContentOnTabDisplay';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { CollectionGrid } from '../../SharedComponents/GridDisplay/CollectionGrid';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import {
    ActiveTabState,
    NavigationContainer
} from '../../SharedComponents/MainPageWithNavigator/MainPageWithNavigator';
import { styles as navigatorStyles } from '../../SharedComponents/MainPageWithNavigator/NavigatorStyles';
import { SortDropdown } from '../../SharedComponents/SortDropdown';
import { IStory, StoryViewStyle } from '../../Stores/StoryStore';
import { Routes, RouteFormats } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { PlaceholderStoryImage, WhitePadLock } from '../../images/images';
import { colors } from '../../styles/colors';

enum OrderBy {
    CreatedNewest = 'Newest',
    CreatedOldest = 'Oldest',
    Family = 'Family Name',
    AlbumName = 'Album Name',
}

interface IProps {
    store: ApplicationStore;
}

interface IState {
    storyContent?: Array<IStory>;
    orderBy: OrderBy;
}

@inject('store')
@observer
export class StoriesPage extends React.Component<IProps & RouteComponentProps<any>, IState> {
    public state: IState = {
        storyContent: undefined,
        orderBy: OrderBy.CreatedOldest,
    };

    public componentDidMount(): void {
        window.scrollTo(0, 0);

        when(
            () =>
                this.props.store.storyStore &&
                this.props.store.storyStore.storiesAreLoaded,
            async () => {
                this.props.store.storyStore.setStoryViewStyle(StoryViewStyle.DEFAULT);
                let scrollTo = 0;
                if (this.props.store.storyStore.storiesPageScrollTo) {
                    scrollTo = this.props.store.storyStore.storiesPageScrollTo;
                }
                window.setTimeout(() => {
                    window.scrollTo(0, scrollTo);
                }, 100);
            },
        );
    }

    public componentWillUnmount(): void {
        this.props.store.storyStore.setStoriesPageScrollTo(window.pageYOffset);
    }

    private onShareClick = (event: React.MouseEvent<HTMLButtonElement>, storyId: number): void => {
        this.props.history.push(RouteFormats.shareStory(storyId));
        event.stopPropagation(); // Don't navigate to the story page (via the click event bubbling out to the story's grid image)
    }

    private renderItem = (story: IStory) => {
        const noPhoto = story.photosWithComments.length === 0;
        const isCurrentUsersStory = story.creator_id === this.props.store.userStore.currentUser.id;
        const route = '/viewstory/' + story.id;

        const getFamilyName = () => {
            if (story.sharedWithFamilyID === null) {
                return ui.YOUR_STORY;
            }
            const family = this.props.store.userFamilyStore.getFamilyByID(story.sharedWithFamilyID);
            return family ? family.name : ui.YOUR_STORY;
        };

        const isEnabled = story.photosWithComments.length > 0 || isCurrentUsersStory;

        const onItemClick = () => {
            if (isEnabled) {
                if (story.photosWithComments.length > 0) {
                    this.props.history.push(route, {story});
                } else {
                    this.props.history.push(Routes.ADD_PHOTOS_TO_STORY.replace(':id', story.id.toString()), {
                        emptyStory: true
                    });
                }
            }
        };

        return (
            <FullContainer
                style={{...styles.storyItemContainer, ...(isEnabled ? {} : styles.disabledLink)}}
                onClick={onItemClick}
            >
                <div style={styles.cardContainer}>
                    <div style={styles.topStoryToolbar}>
                        <p className="TextShadow" style={styles.storyOwner}>
                            {getFamilyName()}
                        </p>
                        {story.isPrivate && (
                            <React.Fragment>
                                {story.photosWithComments.length > 0 &&
                                    <button style={styles.shareButton} onClick={(event) => this.onShareClick(event, story.id)}>
                                        <span style={styles.shareButtonText}>Share</span>
                                    </button>}
                                <img src={WhitePadLock} style={styles.privateLock}/>
                            </React.Fragment>
                        )}
                        <div style={styles.commentCountWrap}>
                            <p style={styles.commentCountText}>{story.commentCount}</p>
                        </div>
                    </div>
                    <div style={styles.topTextContainer}>
                        <p className="TextShadow" style={styles.storyTitleText}>
                            {story.title}
                        </p>
                        <p className="TextShadow"
                           style={styles.photoCountText}>{`${story.photosWithComments.length} photos`}</p>
                    </div>
                </div>

                <img
                    src={noPhoto ? PlaceholderStoryImage : story.photosWithComments[0].photo.thumbnail_url || story.photosWithComments[0].photo.url}
                    style={styles.photoContainer}
                />
            </FullContainer>
        );
    }

    private compareStoriesForSort(): (a: IStory, b: IStory) => number {
        const alphabeticalEndPoint = ''; // this is the lowest character for alpha sort, below z.

        switch (this.props.store.storyStore.storiesPageSort) {
            case OrderBy.CreatedNewest:
                return (a, b) => moment(b.createdAt).diff(moment(a.createdAt));
            case OrderBy.CreatedOldest:
                return (a, b) => moment(a.createdAt).diff(moment(b.createdAt));
            case OrderBy.Family:
                return (a, b) => {
                    if (a.sharedWithFamilyID !== b.sharedWithFamilyID) {
                        const familyA = this.props.store.userFamilyStore.getFamilyByID(a.sharedWithFamilyID);
                        const familyB = this.props.store.userFamilyStore.getFamilyByID(b.sharedWithFamilyID);
                        return (familyA ? familyA.name : alphabeticalEndPoint).localeCompare(familyB ? familyB.name : alphabeticalEndPoint);
                    } else {
                        return a.title.localeCompare(b.title);
                    }
                };
            case OrderBy.AlbumName:
                return (a, b) => a.title.localeCompare(b.title);
            default:
                throw new Error(`Invalid sort order: ${this.props.store.storyStore.storiesPageSort}`);
        }
    }

    public render() {
        if (
            !this.props.store.storyStore ||
          !this.props.store.storyStore.allStories ||
          !this.props.store.userFamilyStore || !this.props.store.userFamilyStore.familiesAreLoaded || !this.props.store.storyStore.storiesAreLoaded
        ) {
            return (
                <NavigationContainer
                    {...this.props}
                    activeTabState={ActiveTabState.ALBUMS}
                    primaryButton={{
                        text: ui.CREATE_ALBUM,
                        action: () => this.props.history.push(Routes.ADD_STORY),
                    }}
                    title={ui.STORY_ALBUMS}
                >
                    <FullContainer style={styles.loadingBackground}>
                        <p style={styles.loadingFont}>{ui.LOADING_ALL}</p>
                    </FullContainer>
                </NavigationContainer>
            );
        }

        const storiesToShow = this.props.store.storyStore.allStories.filter(story => {
            return story.creator_id === this.props.store.userProfileStore.user.id || story.photosWithComments.length > 0;
        });

        const navigationActionButton = <React.Fragment>
            <InformationIcon link={FRESHDESK.CREATE_STORY}
                             style={navigatorStyles.actionButtonInfoIcon}/>
            <CommonActionButton buttonContent={ui.CREATE_ALBUM} onClick={() => this.props.history.push(Routes.ADD_STORY)}
                                containerStyle={{...navigatorStyles.buttonContainer, ...navigatorStyles.actionButtonWhenInfoIconPresent}}
                                fontStyle={navigatorStyles.buttonText}/>
        </React.Fragment>;

        return (
            <NavigationContainer
                {...this.props}
                activeTabState={ActiveTabState.ALBUMS}
                actionButtonAreaChildren={navigationActionButton}
                title={ui.STORY_ALBUMS}
                titleInfoIconHref={FRESHDESK.STORY_ALBUMS}
            >
                <FullContainer>
                    {storiesToShow.length === 0 ? (
                        <EmptyContentOnTabDisplay
                            header={ui.NO_ALBUM}
                            subHeader={ui.SHOULD_CREATE_STORY}
                            infoIconHref={FRESHDESK.CREATE_STORY}
                            buttonText={ui.CREATE_ALBUM}
                            onPress={() => this.props.history.push(Routes.ADD_STORY)}
                        />
                    ) : (
                        <FullContainer>
                            <div style={styles.gridToolsWrap}>
                                <span style={styles.gridInstructions}>
                                    {ui.GRID_INSTRUCTIONS}
                                </span>
                                <SortDropdown
                                    value={this.props.store.storyStore.storiesPageSort}
                                    onChange={orderBy => {
                                        this.props.store.storyStore.setStoriesPageSort(orderBy);
                                    }}
                                    options={[OrderBy.CreatedNewest, OrderBy.CreatedOldest, OrderBy.Family, OrderBy.AlbumName]}
                                    containerStyle={styles.sortDropdownContainer}
                                    style={styles.sortDropdown}
                                />
                            </div>

                            <CollectionGrid
                                keyExtractor={(item: IStory) => item.id}
                                content={storiesToShow.sort(this.compareStoriesForSort())}
                                renderItem={this.renderItem}
                            />
                        </FullContainer>
                    )}
                </FullContainer>
            </NavigationContainer>
        );
    }
}

const styles = {
    loadingFont: {
        fontSize: 20,
        marginBottom: 25,
    },
    gridToolsWrap: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 15,
        paddingLeft: 20,
        paddingRight: 30
    } as React.CSSProperties,
    gridInstructions: {
        color: colors.LIGHT_GREEN_TEXT,
    } as React.CSSProperties,
    sortDropdownContainer: {
        marginLeft: 'auto'
    } as React.CSSProperties,
    sortDropdown: {
        marginTop: 0
    } as React.CSSProperties,
    loadingBackground: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 200,
        flexDirection: 'column',
    } as React.CSSProperties,
    photoContainer: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
    } as React.CSSProperties,
    photoCountText: {
        color: 'white',
        marginBottom: 15,
    },
    storyTitleText: {
        fontWeight: 900,
        fontSize: 19,
        color: 'white',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        paddingLeft: 10,
        marginLeft: -10,
    } as React.CSSProperties,
    topTextContainer: {
        height: 70,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '0 20px',
    } as React.CSSProperties,
    commentCountText: {
        color: '#6F6F6F',
        fontSize: 11,
        fontWeight: 900,
        textAlign: 'center',
        position: 'relative',
        top: 1,
        left: -1,
    } as React.CSSProperties,
    privateLock: {
        height: 16,
    } as React.CSSProperties,
    commentCountWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 25,
        width: 30,
        background: 'no-repeat center/30px url(/images/CommentBubble.png)',
        margin: '0 0.7em',
    } as React.CSSProperties,
    storyOwner: {
        color: 'white',
        fontSize: 13,
        paddingLeft: 22,
        flex: 1,
        display: 'flex',
        alignItems: 'center',
    } as React.CSSProperties,
    shareButton: {
        padding: '0 0.6em',
        backgroundColor: '#CECECE66',
        display: 'flex',
        alignItems: 'center',
        height: '1.2em',
        cursor: 'pointer',
        backdropFilter: 'blur(10px)',
        marginLeft: '0.7em',
        marginRight: '0.2em'
    } as React.CSSProperties,
    shareButtonText: {
        fontFamily: 'ProximaNova, Arial, sans-serif',
        color: '#fff',
        fontSize: '0.6em',
    },
    topStoryToolbar: {
        height: 30,
        width: '100%',
        marginTop: 9,
        display: 'flex',
        alignItems: 'center',
    } as React.CSSProperties,
    cardContainer: {
        zIndex: 900,
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    } as React.CSSProperties,
    storyItemContainer: {
        position: 'relative',
        cursor: 'pointer',
    } as React.CSSProperties,
    disabledLink: {
        cursor: 'not-allowed',
    } as React.CSSProperties,
};



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/StoriesPage.tsx